import { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Upload, X, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";

const SortableThumb = ({ id, fileId, onRemove, testKey }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`${testKey}-thumb-${id}`}>
      <img
        src={`${API_BASE}/files/${fileId}`}
        alt=""
        className="w-24 h-24 rounded-md object-cover border border-border"
      />
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 w-6 h-6 rounded bg-white/90 text-foreground flex items-center justify-center shadow cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
        data-testid={`${testKey}-thumb-handle-${id}`}
      >
        <GripVertical size={12} />
      </button>
      <button
        type="button"
        onClick={() => onRemove(id)}
        className="absolute top-1 right-1 w-6 h-6 rounded bg-destructive text-destructive-foreground flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Remove image"
        data-testid={`${testKey}-thumb-remove-${id}`}
      >
        <X size={12} />
      </button>
    </div>
  );
};

/**
 * Multi-image uploader with drag-and-drop file drops AND drag-to-reorder thumbnails.
 *
 * Props:
 * - value: array of file ids
 * - onChange: (ids: string[]) => void
 * - testKey: data-testid prefix
 */
const MultiImageUploader = ({ value = [], onChange, testKey = "imgs" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const uploaded = [];
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: not an image`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
          const res = await api.post(`/files/upload/image`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push(res.data.id);
        } catch (err) {
          toast.error(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`);
        }
      }
      if (uploaded.length) {
        onChange?.([...(value || []), ...uploaded]);
        toast.success(`Uploaded ${uploaded.length} image${uploaded.length === 1 ? "" : "s"}`);
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPick = (e) => uploadFiles(Array.from(e.target.files || []));

  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeOne = (id) => onChange?.((value || []).filter((v) => v !== id));

  const onDragEnd = ({ active, over: ov }) => {
    if (!ov || active.id === ov.id) return;
    const oldIndex = value.indexOf(active.id);
    const newIndex = value.indexOf(ov.id);
    onChange?.(arrayMove(value, oldIndex, newIndex));
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed transition-colors p-6 text-center ${
          over ? "border-accent bg-accent/10" : "border-border bg-muted/40 hover:bg-muted/60"
        }`}
        data-testid={`${testKey}-dropzone`}
      >
        {busy ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" /> Uploading...
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-sm">
            <Upload size={20} className="text-accent" />
            <div className="font-medium">Drag & drop images here</div>
            <div className="text-xs text-muted-foreground">or click to browse · up to 10 MB each</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onPick}
          data-testid={`${testKey}-input`}
        />
      </div>

      {value && value.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={value} strategy={rectSortingStrategy}>
            <div className="flex flex-wrap gap-2" data-testid={`${testKey}-thumbs`}>
              {value.map((id) => (
                <SortableThumb key={id} id={id} fileId={id} onRemove={removeOne} testKey={testKey} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {value && value.length > 1 && (
        <p className="text-xs text-muted-foreground">Tip: drag the handle on a thumbnail to reorder.</p>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testKey}-pick-more-btn`}
        >
          <Upload size={14} className="mr-1.5" /> Add more
        </Button>
      </div>
    </div>
  );
};

export default MultiImageUploader;
